import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
// FR-012 lazy load (DECIDED 2026-09-04, e2e finding F1): the /pure entrypoint
// has NO import-time side effect — the default "@stripe/stripe-js" entry
// injects the Stripe.js script the moment the module is imported, which
// fetched Stripe.js on every route (admin CSP blocks it). With /pure the
// script loads only when loadStripe() runs, and loadStripe runs only on
// payment-step mount (getStripeLoader below).
import { loadStripe } from "@stripe/stripe-js/pure";
import type { ReactElement } from "react";
import type { MutableRefObject } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui";

/**
 * App-styled payment-step modal (CLMS-117 T033). Renders the Stripe PAYMENT
 * ELEMENT (provider-tokenized entry; the server's named payment-method
 * configuration decides the offered methods — card only today, the SPA never
 * lists or filters methods itself) inside the shared Radix dialog (focus
 * trap, labelled title/description). No hosted redirect checkout is used
 * (FR-012): confirmation runs in-page with `redirect: "if_required"` and no
 * return/provider URL is ever constructed. The client secret arrives via
 * props, is handed only to Stripe.js (via the Elements options, which hold a
 * copy in hook state while the modal is mounted), and is never logged,
 * persisted, or placed in a URL. Failure messages are user-safe categories
 * only (FR-022) — provider error detail never renders.
 */

/** One Stripe.js load per publishable key; never re-created per render. */
const stripeLoaderCache = new Map<string, Promise<Stripe | null>>();

function getStripeLoader(publishableKey: string): Promise<Stripe | null> {
  let loader = stripeLoaderCache.get(publishableKey);
  if (!loader) {
    const created = loadStripe(publishableKey);
    loader = created;
    stripeLoaderCache.set(publishableKey, created);
    // Only a usable Stripe instance stays cached: a rejected or resolved-null
    // load is evicted so a later modal open retries instead of replaying the
    // failure for the rest of the session. Guarded so a retry that already
    // replaced the entry is never evicted by the stale loader settling.
    const evict = (): void => {
      if (stripeLoaderCache.get(publishableKey) === created) {
        stripeLoaderCache.delete(publishableKey);
      }
    };
    created.then((stripe) => {
      if (stripe === null) {
        evict();
      }
    }, evict);
  }
  return loader;
}

export type PaymentFailureCategory = "payment_details" | "payment_unavailable";

export const PAYMENT_FAILURE_COPY: Record<PaymentFailureCategory, string> = {
  payment_details: "Your payment could not be completed. Check your payment details and try again.",
  payment_unavailable: "We couldn't process your payment right now. Try again in a moment.",
};

/**
 * Stripe.js failed to load (blocked script, network): the form can never
 * mount and there is no retry, so the copy names the one available action and
 * makes no payment-state claim.
 */
export const STRIPE_LOAD_FAILURE_COPY = {
  title: "Payment form unavailable",
  description:
    "We couldn't load the secure payment form. Choose Pay later; you can pay your dues invoice any time after signing in.",
} as const;

export interface RegistrationPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Server-resolved display amount from the status API, shown verbatim. */
  amountDue: string;
  /** Secret of the server-created PaymentIntent (memory-only pass-through). */
  clientSecret: string;
  publishableKey: string;
  /** Provider confirmation accepted — parent shows verification-pending and resumes polling. */
  onConfirmed: () => void;
  /**
   * Mirrors the in-flight confirmPayment state to the parent so a
   * parent-initiated teardown (e.g. an authority-lost revert from a
   * background 401) can be deferred until the confirmation settles —
   * unmounting the Elements iframe mid-3DS would abort the confirm.
   */
  onConfirmingChange?: (confirming: boolean) => void;
  /**
   * `true` when a confirm is dispatched; on a definitive provider decline
   * (card/validation error) it reports the value from before that confirm.
   * Any other failure leaves the outcome unknown — the payment may already
   * have reached the provider — so the parent's Pay later renders the
   * pending-payment surface instead of the deferred one (UI contract
   * "Pay later disposition").
   */
  onPaymentBegunChange?: (begun: boolean) => void;
}

export function RegistrationPaymentModal({
  open,
  onOpenChange,
  amountDue,
  clientSecret,
  publishableKey,
  onConfirmed,
  onConfirmingChange,
  onPaymentBegunChange,
}: RegistrationPaymentModalProps): ReactElement {
  // Lifted so dismissal can be blocked while confirmPayment is in flight:
  // closing then would unmount the Elements iframe mid-confirmation (e.g.
  // a 3DS challenge) and onConfirmed could fire after a "cancelled" close.
  const [confirming, setConfirming] = useState(false);
  const handleConfirmingChange = (next: boolean): void => {
    setConfirming(next);
    onConfirmingChange?.(next);
  };
  // The begun value this modal last reported. A confirm that ends in a
  // definitive decline restores the value from BEFORE that confirm (not
  // `false`): an earlier confirm whose outcome is unknown still counts.
  const paymentBegunRef = useRef(false);
  const handlePaymentBegunChange = (begun: boolean): void => {
    paymentBegunRef.current = begun;
    onPaymentBegunChange?.(begun);
  };
  const [stripeLoader, setStripeLoader] = useState(() => getStripeLoader(publishableKey));
  // Re-acquire the loader when the modal opens: a no-op while the cached load
  // is healthy (same promise, no re-render). The modal opens at most once per
  // payment step (closing it is Pay later), so this retries a Stripe.js load
  // that failed, and was evicted from the cache, before Pay now was clicked.
  useEffect(() => {
    if (open) {
      setStripeLoader(getStripeLoader(publishableKey));
    }
  }, [open, publishableKey]);
  // Stripe.js failing to load (script blocked, network) or resolving null
  // would otherwise leave the Pay button disabled forever with no feedback;
  // detect it and surface the STRIPE_LOAD_FAILURE_COPY alert instead.
  const [stripeLoadFailed, setStripeLoadFailed] = useState(false);
  useEffect(() => {
    let disposed = false;
    setStripeLoadFailed(false);
    stripeLoader.then(
      (stripe) => {
        if (!disposed && stripe === null) {
          setStripeLoadFailed(true);
        }
      },
      () => {
        if (!disposed) {
          setStripeLoadFailed(true);
        }
      },
    );
    return () => {
      disposed = true;
    };
  }, [stripeLoader]);
  // Scope of the secret here: this memo (the Elements options) holds a copy
  // in hook state for as long as the modal is mounted; it is released only
  // when the modal unmounts (Pay later, modal close, confirmed, terminal).
  const options = useMemo(() => {
    // Stripe's appearance API needs a literal value: resolve the shared
    // --clm-primary-blue token at mount, falling back to its literal only
    // where it isn't defined (e.g. jsdom).
    const colorPrimary =
      getComputedStyle(document.documentElement).getPropertyValue("--clm-primary-blue").trim() ||
      "#16456b";
    return {
      clientSecret,
      appearance: {
        variables: { colorPrimary, fontSizeBase: "14px" },
      },
    };
  }, [clientSecret]);

  const handleOpenChange = (nextOpen: boolean): void => {
    // Grant-form pattern: ignore Escape/overlay/X closes during confirm.
    if (confirming) {
      return;
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby="registration-payment-description"
        onEscapeKeyDown={(event) => {
          if (confirming) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (confirming) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Pay your membership dues</DialogTitle>
          <DialogDescription id="registration-payment-description" className="text-gray-600">
            Amount due: <span className="font-medium text-gray-900">{amountDue}</span>. Payment is
            processed securely by our payment provider; card details never reach CLM. Closing this
            window chooses Pay later.
          </DialogDescription>
        </DialogHeader>
        {stripeLoadFailed ? (
          // Initialization failed: the Payment Element can never mount, so
          // the form would be a permanently-disabled dead end. Its own
          // user-safe copy (not the confirm-time payment_unavailable text,
          // which says "try again"): there is no in-session retry after this
          // failure, no payment was attempted, and the only exit is Pay later
          // (closing the modal is Pay later).
          <>
            <Alert variant="destructive" className="mt-4">
              <AlertTitle>{STRIPE_LOAD_FAILURE_COPY.title}</AlertTitle>
              <AlertDescription>{STRIPE_LOAD_FAILURE_COPY.description}</AlertDescription>
            </Alert>
            <DialogFooter className="mt-4">
              <Button type="button" className="w-full" onClick={() => handleOpenChange(false)}>
                Pay later
              </Button>
            </DialogFooter>
          </>
        ) : (
          <Elements stripe={stripeLoader} options={options}>
            <PaymentModalForm
              amountDue={amountDue}
              submitting={confirming}
              onSubmittingChange={handleConfirmingChange}
              onConfirmed={onConfirmed}
              paymentBegunRef={paymentBegunRef}
              onPaymentBegunChange={handlePaymentBegunChange}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PaymentModalForm({
  amountDue,
  submitting,
  onSubmittingChange,
  onConfirmed,
  paymentBegunRef,
  onPaymentBegunChange,
}: {
  amountDue: string;
  submitting: boolean;
  onSubmittingChange: (submitting: boolean) => void;
  onConfirmed: () => void;
  paymentBegunRef: MutableRefObject<boolean>;
  onPaymentBegunChange: (begun: boolean) => void;
}): ReactElement {
  const stripe = useStripe();
  const elements = useElements();
  const [failureCategory, setFailureCategory] = useState<PaymentFailureCategory | null>(null);

  const handleConfirm = async (): Promise<void> => {
    if (!stripe || !elements || submitting) {
      return;
    }
    onSubmittingChange(true);
    const begunBeforeConfirm = paymentBegunRef.current;
    onPaymentBegunChange(true);
    setFailureCategory(null);
    try {
      // In-page confirmation only; card payments complete without redirect.
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {},
        redirect: "if_required",
      });
      if (error) {
        // Closed user-safe category mapping — provider messages never render.
        const declined = error.type === "card_error" || error.type === "validation_error";
        if (declined) {
          // A decline began no payment: leave the begun state as it was
          // before this confirm (an earlier unknown-outcome confirm stays).
          onPaymentBegunChange(begunBeforeConfirm);
        }
        setFailureCategory(declined ? "payment_details" : "payment_unavailable");
        return;
      }
      onConfirmed();
    } catch {
      // Thrown confirm: the outcome is unknown, so the begun state set above
      // is deliberately left true.
      setFailureCategory("payment_unavailable");
    } finally {
      onSubmittingChange(false);
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void handleConfirm();
      }}
    >
      <PaymentElement />
      {failureCategory && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Payment not completed</AlertTitle>
          <AlertDescription>{PAYMENT_FAILURE_COPY[failureCategory]}</AlertDescription>
        </Alert>
      )}
      {/* SR-visible confirmation progress (established audit pattern). */}
      <p role="status" className="mt-2 text-sm text-gray-600">
        {submitting ? "Processing your payment\u2026" : null}
      </p>
      <DialogFooter className="mt-4">
        {/* Disabled until Stripe AND Elements are ready: an enabled click
            before elements resolves would silently no-op in handleConfirm. */}
        <Button type="submit" className="w-full" disabled={!stripe || !elements || submitting}>
          Pay {amountDue}
        </Button>
      </DialogFooter>
    </form>
  );
}
